import React, { useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  downloadJSON,
  getModels,
  Model,
  MODEL_DEFAULT_PERMISSION,
  ModelView,
  setBatchProcessLimit,
  setMetadataField,
  removeMetadataField,
  updateModelPermission,
  validateAndUpsertModel,
} from "src/store/modelManagementSlice";
import editTokens from "@amzn/meridian-tokens/base/icon/edit";
import plusToken from "@amzn/meridian-tokens/base/icon/plus";
import trashToken from "@amzn/meridian-tokens/base/icon/trash";
import "src/components/shared/recordLine/recordLine.scss";
import Button from "@amzn/meridian/button";
import { useNavigate } from "react-router-dom";
import Tag from "@amzn/meridian/tag";
import Input from "@amzn/meridian/input";
import Icon from "@amzn/meridian/icon";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import Textarea from "@amzn/meridian/textarea";
import { isAdminUser } from "src/utils/permissionUtil";
import LabelHierarchyModal from "src/components/pages/modelManagement/LabelHierarchyModal/labelHierarchyModal";
import ValidationErrorModal from "src/components/pages/modelManagement/ValidationErrorModal";
import * as toast from "src/utils/toastHelper";

interface ModelViewListProps {
  modelView: ModelView;
  onClick: () => void;
}

const ModelViewList: React.FC<ModelViewListProps> = ({ modelView, onClick }) => {
  const { selectedModel, selectedModelId, errors } = useAppSelector((state) => state.modelSlice);
  const [aliasList, setAliasList] = useState<string[]>([]);
  const [aliasListString, setaliasListString] = useState("");

  // Update aliasList and aliasListString when modelView changes
  useEffect(() => {
    const permissionValue = modelView.permissions?.[MODEL_DEFAULT_PERMISSION];
    let newAliasList: string[] = [];

    if (permissionValue) {
      if (Array.isArray(permissionValue)) {
        newAliasList = permissionValue;
      } else {
        try {
          newAliasList = Array.from(permissionValue);
        } catch (e) {
          console.error("Error converting permissions:", e);
          newAliasList = [];
        }
      }
      setAliasList(newAliasList);
      setaliasListString(newAliasList.join(";"));
    }
  }, [modelView]);

  const dispatch = useAppDispatch();
  const [editPermission, setEditPermission] = useState(false);
  const [editBatchLimit, setEditBatchLimit] = useState(false);
  const [editMetadata, setEditMetadata] = useState(false);
  const [newMetadataKey, setNewMetadataKey] = useState("");
  const [newMetadataValue, setNewMetadataValue] = useState("");
  const [isLabelHierarchyModalOpen, setIsLabelHierarchyModalOpen] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);

  useEffect(() => {
    setShowValidationModal(false);
  }, [selectedModelId]);

  const handleSave = async (model: Model, onSuccess: () => void, errorMsg: string) => {
    const result = await dispatch(validateAndUpsertModel(model));
    if (isFulfilled(result)) {
      onSuccess();
      dispatch(getModels());
    } else if (isRejected(result) && result.meta.rejectedWithValue) {
      setShowValidationModal(true);
    } else {
      toast.error(errorMsg);
    }
  };

  const { alias, ldap } = useAppSelector((state) => state.user);

  const navigate = useNavigate();

  const selected = modelView.id === selectedModelId;

  const getDescription = (modelView: ModelView) => {
    return `${modelView.numberOfLabels} Fields`;
  };

  const getModelAccessTag = () => {
    // Owner check
    if (modelView.ownerAlias === alias) {
      return <Tag type="success">Owner</Tag>;
    }

    // Check if user has explicit permission in the model
    const permissions = modelView.permissions?.[MODEL_DEFAULT_PERMISSION];
    const hasAccess = Array.isArray(permissions)
      ? permissions.includes(alias) || ldap.some((group) => permissions.includes(group))
      : permissions?.has?.(alias) || ldap.some((group) => permissions?.has?.(group));

    if (hasAccess) {
      return <Tag type="theme">Shared</Tag>;
    }

    // Admin check
    if (isAdminUser(ldap)) {
      return <Tag type="neutral">Admin</Tag>;
    }
  };

  return (
    <div key={modelView.id} className="row-style" onClick={onClick}>
      {selected ? (
        <>
          <Row className={"selected-color"} spacing={"0"} widths={["justify", "70%", "justify"]}>
            <div className="pre-line"></div>
            <Column spacing={"100"} spacingInset={"200"}>
              <Text type={selected ? "h50" : "b200"}>{modelView.name}</Text>
              <Text type="b200" color="secondary" truncate>
                {getDescription(modelView)}
              </Text>
            </Column>
            <Column spacing={"100"} spacingInset={"200"} alignmentHorizontal={"end"}>
              {getModelAccessTag()}
            </Column>
          </Row>
          {selected && (
            <Row className={"selected-color"} widths={["justify", "40%", "5%", "40%"]}>
              <div className="pre-line"></div>
              <Button
                onClick={() => navigate("/validationWorkflow")}
                type={"tertiary"}
                size={"small"}
              >
                validate model
              </Button>
              <div></div>
              <Button
                onClick={() => dispatch(downloadJSON(selectedModel))}
                type={"tertiary"}
                size={"small"}
              >
                export model
              </Button>
            </Row>
          )}
          {(alias === modelView.ownerAlias || isAdminUser(ldap)) && (
            <Row
              className={"selected-color"}
              spacing={"300"}
              widths={["justify", "80%"]}
              width={"100%"}
            >
              <div className="pre-line-long"></div>
              <>
                <Column spacing={"0"}>
                  <Row>
                    <Text type={"b100"}>Shared with user alias:</Text>
                  </Row>
                  <Row>
                    <Textarea
                      size={"medium"}
                      width={"100%"}
                      value={aliasListString}
                      rows={1}
                      helperText={"Enter multiple alias separate by ;"}
                      disabled={!editPermission}
                      onChange={(value) => {
                        setaliasListString(value);
                        dispatch(updateModelPermission(value));
                      }}
                    />
                  </Row>
                </Column>
                {editPermission ? (
                  <Button
                    onClick={async () => {
                      await handleSave(
                        selectedModel,
                        () => setEditPermission(false),
                        "Failed to save permissions. Please try again.",
                      );
                    }}
                    size={"small"}
                  >
                    Save
                  </Button>
                ) : (
                  <Button onClick={() => setEditPermission(true)} type={"icon"}>
                    <Icon tokens={editTokens}></Icon>
                  </Button>
                )}
              </>
            </Row>
          )}
          {isAdminUser(ldap) && (
            <Row
              className={"selected-color"}
              spacing={"300"}
              widths={["justify", "70%", "justify"]}
              alignmentVertical={"bottom"}
            >
              <div className="pre-line-long"></div>
              <Column spacing={"100"} spacingInset={"200"}>
                <Text type={"b100"}>edit batch process limit</Text>
                <Input
                  value={selectedModel.batchProcessLimit}
                  onChange={(value) => dispatch(setBatchProcessLimit(value))}
                  type="number"
                  placeholder=""
                  disabled={!editBatchLimit}
                />
              </Column>
              <Column spacing={"100"} spacingInset={"200"}>
                {editBatchLimit ? (
                  <Button
                    onClick={async () => {
                      await handleSave(
                        selectedModel,
                        () => setEditBatchLimit(false),
                        "Failed to save batch process limit. Please try again.",
                      );
                    }}
                    size={"small"}
                  >
                    Save
                  </Button>
                ) : (
                  <Button onClick={() => setEditBatchLimit(true)} type={"icon"}>
                    <Icon tokens={editTokens}></Icon>
                  </Button>
                )}
              </Column>
            </Row>
          )}
          {isAdminUser(ldap) && (
            <Row
              className={"selected-color"}
              spacing={"300"}
              widths={["justify", "100%"]}
              width={"100%"}
            >
              <div className="pre-line-long"></div>
              <Column spacing={"200"} spacingInset={"200"} width={"100%"}>
                <Row alignmentHorizontal={"start"} alignmentVertical={"center"}>
                  <Text type={"b100"}>Model Metadata (Key-Value Pairs)</Text>
                  <Button onClick={() => setEditMetadata(!editMetadata)} type={"icon"}>
                    <Icon tokens={editTokens}></Icon>
                  </Button>
                </Row>

                {/* Display existing metadata */}
                {selectedModel.metadata && Object.keys(selectedModel.metadata).length > 0 && (
                  <Column spacing={"100"}>
                    {Object.entries(selectedModel.metadata).map(([key, value]) => (
                      <Row key={key} alignmentVertical={"center"} widths={["30%", "60%", "10%"]}>
                        <Input
                          value={key}
                          onChange={(newKey) => {
                            if (editMetadata && newKey !== key) {
                              // Remove old key and add new key
                              dispatch(removeMetadataField(key));
                              dispatch(setMetadataField({ key: newKey, value }));
                            }
                          }}
                          type="text"
                          placeholder="Key"
                          disabled={!editMetadata}
                          size="small"
                        />
                        <Input
                          value={String(value)}
                          onChange={(newValue) => {
                            if (editMetadata) {
                              dispatch(setMetadataField({ key, value: newValue }));
                            }
                          }}
                          type="text"
                          placeholder="Value"
                          disabled={!editMetadata}
                          size="small"
                        />
                        {editMetadata && (
                          <Button
                            type={"icon"}
                            size="small"
                            onClick={() => dispatch(removeMetadataField(key))}
                          >
                            <Icon tokens={trashToken}></Icon>
                          </Button>
                        )}
                      </Row>
                    ))}
                  </Column>
                )}

                {/* Add new metadata field */}
                {editMetadata && (
                  <Column spacing={"100"}>
                    <Row alignmentVertical={"center"} widths={["30%", "60%", "10%"]}>
                      <Input
                        value={newMetadataKey}
                        onChange={setNewMetadataKey}
                        type="text"
                        placeholder="New Key"
                        size="small"
                      />
                      <Input
                        value={newMetadataValue}
                        onChange={setNewMetadataValue}
                        type="text"
                        placeholder="New Value"
                        size="small"
                      />
                      <Button
                        type={"icon"}
                        size="small"
                        disabled={!newMetadataKey.trim() || !newMetadataValue.trim()}
                        onClick={() => {
                          if (newMetadataKey.trim() && newMetadataValue.trim()) {
                            dispatch(
                              setMetadataField({
                                key: newMetadataKey.trim(),
                                value: newMetadataValue.trim(),
                              }),
                            );
                            setNewMetadataKey("");
                            setNewMetadataValue("");
                          }
                        }}
                      >
                        <Icon tokens={plusToken}></Icon>
                      </Button>
                    </Row>

                    <Row alignmentHorizontal={"start"}>
                      <Button
                        onClick={async () => {
                          // Create a copy of the model with updated metadata
                          let updatedModel = { ...selectedModel };

                          // Add any pending new metadata fields to the copy
                          if (newMetadataKey.trim() && newMetadataValue.trim()) {
                            updatedModel = {
                              ...updatedModel,
                              metadata: {
                                ...updatedModel.metadata,
                                [newMetadataKey.trim()]: newMetadataValue.trim(),
                              },
                            };

                            // Also update the Redux state
                            dispatch(
                              setMetadataField({
                                key: newMetadataKey.trim(),
                                value: newMetadataValue.trim(),
                              }),
                            );
                          }

                          // Use the updated model copy for saving
                          await handleSave(
                            updatedModel,
                            () => {
                              setEditMetadata(false);
                              setNewMetadataKey("");
                              setNewMetadataValue("");
                            },
                            "Failed to save metadata. Please try again.",
                          );
                        }}
                        size={"small"}
                        type="tertiary"
                      >
                        Save Metadata
                      </Button>
                    </Row>
                  </Column>
                )}

                {!selectedModel.metadata || Object.keys(selectedModel.metadata).length === 0 ? (
                  <Text type="b200" color="secondary">
                    No metadata fields defined. Only admin and engineers can edit metadata.
                  </Text>
                ) : null}
              </Column>
            </Row>
          )}

          {isAdminUser(ldap) && (
            <Row
              className={"selected-color"}
              spacing={"300"}
              widths={["justify", "100%"]}
              width={"100%"}
            >
              <div className="pre-line-long"></div>
              <Column spacing={"200"} spacingInset={"200"} width={"100%"}>
                <Row alignmentHorizontal="start" alignmentVertical="center">
                  <Text type={"b100"}>Manage Field Hierarchy</Text>
                  <Button
                    onClick={() => setIsLabelHierarchyModalOpen(true)}
                    type="icon"
                    size="small"
                  >
                    <Icon tokens={editTokens}></Icon>
                  </Button>
                </Row>
              </Column>
            </Row>
          )}

          <LabelHierarchyModal
            modelId={modelView.id}
            isOpen={isLabelHierarchyModalOpen}
            onClose={() => setIsLabelHierarchyModalOpen(false)}
          />
          <ValidationErrorModal
            open={showValidationModal}
            onClose={() => setShowValidationModal(false)}
            errors={errors}
            model={selectedModel}
          />
        </>
      ) : (
        <Row className={"unselected-color"} spacing={"300"} widths={["justify", "70%", "justify"]}>
          <div></div>
          <Column spacing={"100"} spacingInset={"200"}>
            <Text type={selected ? "h50" : "b200"}>{modelView.name}</Text>
            <Text type="b200" color="secondary" truncate>
              {getDescription(modelView)}
            </Text>
          </Column>
          {getModelAccessTag()}
        </Row>
      )}
    </div>
  );
};

export default ModelViewList;
